import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  InputGroup,
  Table,
  DOMHelper,
  Stack,
  SelectPicker,
  Panel,
  Checkbox,
  IconButton,
  Pagination,
  Whisper,
  Tooltip,
  Message,
  useToaster,
  Modal,
  Button,
  InputPicker,
  TagPicker,
  DatePicker,
  Form
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import MoreIcon from '@rsuite/icons/legacy/More';
import DrawerView from './DrawerView';
import { NameCell, ImageCell, CheckCell } from './Cells';
import { Edit, Gear, Icon } from '@rsuite/icons';
import { MdOutlineDeleteOutline, MdPersonAdd, MdNotifications } from 'react-icons/md';
import { CiImport, CiExport } from 'react-icons/ci';
import { useNavigate } from 'react-router-dom';
import {
  getAdminUsers,
  removeUser,
  blockUser,
  updateUser,
  getUsersWithoutDispatch
} from '@/services/user.service';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { getGrades } from '@/services/grade.service';
import { getServicelines } from '@/services/serviceline.service';
import { RiUserFollowFill, RiUserForbidFill } from 'react-icons/ri';
import { TbLockCheck, TbLockCancel } from 'react-icons/tb';
import * as XLSX from 'xlsx';
import ConfirmModal from '@/components/ConfirmModal';
import { FaUserEdit } from 'react-icons/fa';
import { GrRevert } from 'react-icons/gr';
import DrawerUpdate from './DrawerUpdate';
import ModalUsersMass from './ModalUsersMass';
import NotificationSettingsDrawer from '../tables/NotificationSettingsDrawer';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const toaster = useToaster();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [showDrawer, setShowDrawer] = useState(false);
  const [sortColumn, setSortColumn] = useState();
  const [sortType, setSortType] = useState();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [grade, setGrade] = useState(null);
  const [sl, setSl] = useState(null);
  const [status, setStatus] = useState(null);
  const [resignationDate, setResignationDate] = useState<Date | null>(null);
  const [showNotificationDrawer, setShowNotificationDrawer] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    subtitle: '',
    onConfirm: () => {}
  });
  const [open, setOpen] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);
  const [showSelectAllModal, setShowSelectAllModal] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [searchUsers, setSearchUsers] = useState([]);
  const [filterFields, setFilterFields] = useState({});
  const [loadingExport, setLoadingExport] = useState(false);

  const users = useAppSelector(state => state.users.data) || [];
  const allUsers = useAppSelector(state => state.users.total);
  const grades =
    useAppSelector(state => state.grade.data)?.map(item => ({
      value: item._id,
      label: item.grade_name
    })) || [];
  const servicelines =
    useAppSelector(state => state.serviceLine.data)?.map(item => ({
      value: item._id,
      label: item.serviceLine
    })) || [];
  const loading = useAppSelector(state => state.users.loading);

  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [checkedKeys, setCheckedKeys] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(getGrades());
      await dispatch(getServicelines());
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const resultUsers = await getUsersWithoutDispatch();
      setUsersList(resultUsers);
    };
    fetchData();
  }, [showDrawer]);

  // Apply frontend filtering
  const filteredData = (dataSource = users) => {
    let filtered = Array.isArray(dataSource) ? dataSource : [];
    // if (searchKeyword || sl || grade || status != null) {
    //   filtered = dataSource.filter(item => {
    //     if (!item) return false;
    //     if (
    //       !(item.firstname?.toLowerCase() + ' ' + item.lastname?.toLowerCase()).includes(
    //         searchKeyword.toLowerCase()
    //       )
    //     ) {
    //       return false;
    //     }
    //     if (sl && item.serviceLine?._id !== sl) {
    //       return false;
    //     }
    //     if (grade && item.grade?._id !== grade) {
    //       return false;
    //     }
    //     if (status != null) {
    //       return (item.isBlocked === false || item.isBlocked === undefined) !== status;
    //     }
    //     return true;
    //   });
    // }
    if (sortColumn && sortType) {
      return filtered.sort((a, b) => {
        let x = a[sortColumn];
        let y = b[sortColumn];
        if (typeof x === 'string') x = x?.charCodeAt(0) || 0;
        if (typeof y === 'string') y = y?.charCodeAt(0) || 0;
        return sortType === 'asc' ? x - y : y - x;
      });
    }
    return filtered;
  };

  // Use backend-paginated or full data
  const data = filteredData();

  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
    setCheckedKeys([]);
  };

  const handleSortColumn = (sortColumn, sortType) => {
    setSortColumn(sortColumn);
    setSortType(sortType);
    setCheckedKeys([]);
  };

  const handleNotificationSettings = () => {
    setShowNotificationDrawer(true);
  };

  const handleCheckAll = (value, checked) => {
    if (checked && limit != 'all') {
      setShowSelectAllModal(true);
    } else {
      const keys = checked ? data.map(item => item._id).filter(Boolean) : [];
      setCheckedKeys(keys);
    }
  };

  const handleCheck = (value, checked) => {
    const keys = checked ? [...checkedKeys, value] : checkedKeys.filter(item => item !== value);
    setCheckedKeys(keys);
  };

  const handleSelectCurrentPage = () => {
    const keys = data.map(item => item._id).filter(Boolean);
    setCheckedKeys(keys);
    setShowSelectAllModal(false);
  };

  const handleSelectAllData = async () => {
    try {
      setLimit('all');
      setPage(1);
      const allData = await dispatch(getAdminUsers({ limit: 'all' }));
      const allKeys = filteredData(allData)
        .map(item => item._id)
        .filter(Boolean);
      setCheckedKeys(allKeys);
      setShowSelectAllModal(false);
    } catch (error) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t('Failed to select all users: {{error}}', { error: error.message })}
        </Message>,
        { placement: 'topCenter' }
      );
      setCheckedKeys([]);
    }
  };

  // Update allUsers on filter change without resetting
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const filter: any = {};
        if (grade) {
          filter.grade = grade;
        }
        if (sl) {
          filter.sl = sl;
        }
        if (status) {
          filter.status = status;
        }
        if (searchKeyword?.length > 0) {
          filter.search = searchKeyword;
        }
        if (searchUsers) {
          filter.searchUsers = searchUsers;
        }
        setFilterFields(filter);
        await dispatch(getAdminUsers({ limit, page }, filter));
      } catch (error) {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            {t('Failed to fetch all users: {{error}}', { error: error.message })}
          </Message>,
          { placement: 'topCenter' }
        );
      }
    };
    fetchAllUsers();
  }, [dispatch, searchKeyword, grade, sl, status, page, limit, searchUsers]);

  const handleRevertToPaginated = () => {
    setLimit(20);
    setPage(1);
    setCheckedKeys([]);
  };

  let checked = false;
  let indeterminate = false;
  if (data?.length > 0) {
    if (checkedKeys.length === data.length) {
      checked = true;
    } else if (checkedKeys.length > 0) {
      indeterminate = true;
    }
  }

  const renderMenuItem = (label, item) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {item.icon} <span>{label}</span>
    </div>
  );

  const downloadExcel = (dataUsers, name) => {
    const worksheet = XLSX.utils.json_to_sheet(dataUsers);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
    setLoadingExport(false);
  };

  const handleExport = async () => {
    setLoadingExport(true);
    const result = await dispatch(getAdminUsers({ limit: null, page: null }, filterFields));

    const usersToExport = filteredData(result).map(user => ({
      FirstName: user.firstname || '',
      LastName: user.lastname || '',
      Email: user.Email || '',
      RegistrationNumber: user.registrationNumber || '',
      'Phone number': user.telephone || '',
      Grade: user.grade?.grade_name || '',
      Team: user.serviceLine?.serviceLine || '',
      Manager: user.manager ? user.manager.firstname + ' ' + user.manager.lastname : '',
      'Second Manager': user.secondValidator
        ? user.secondValidator.firstname + ' ' + user.secondValidator.lastname
        : '',
      'Hire date': user.hire_date ? new Date(user.hire_date) : ''
    }));
    downloadExcel(usersToExport, 'users');
  };

  const handleToggleBlock = async (id, isBlocked) => {
    try {
      await dispatch(blockUser(id, { isBlocked: !isBlocked }));
      toaster.push(
        <Message type="success" duration={5000} showIcon>
          {t(isBlocked ? 'User successfully unblocked' : 'User successfully blocked')}
        </Message>,
        { placement: 'topCenter' }
      );
      const filter: any = { status: status || false, grade, sl };
      if (searchKeyword?.length > 0) {
        filter.search = searchKeyword;
      }
      await dispatch(getAdminUsers({ limit, page }, filter));
    } catch (error) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(error.message)}
        </Message>,
        { placement: 'topCenter' }
      );
    }
    setConfirmModal({ open: false });
  };
  const handleSetResignationAndBlock = async (id: string, resignationDate: Date) => {
    try {
      // console.log('Submitting resignation_date:', resignationDate);
      const formData = new FormData();
      formData.append('resignation_date', resignationDate.toISOString());
      formData.append('isBlocked', 'true');
      await dispatch(updateUser(id, formData));

      toaster.push(
        <Message type="success" duration={5000} showIcon>
          {t('User blocked and resignation date set successfully')}
        </Message>,
        { placement: 'topCenter' }
      );

      setPage(1);
      setCheckedKeys([]);
      const filter: any = {};
      if (status !== null) {
        filter.status = status;
      }
      if (grade) {
        filter.grade = grade;
      }
      if (sl) {
        filter.sl = sl;
      }
      if (searchKeyword?.length > 0) {
        filter.search = searchKeyword;
      }
      // console.log('Fetching users with filter:', filter, 'limit:', limit, 'page:', 1);
      const response = await dispatch(getAdminUsers({ limit, page: 1 }, filter));
      // console.log('getAdminUsers response:', response);
    } catch (error) {
      console.error('Update or refresh error:', error);
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(error.message || 'Failed to update user or refresh data')}
        </Message>,
        { placement: 'topCenter' }
      );
    }
    setConfirmModal({ open: false });
  };
  const handleDelete = async id => {
    try {
      await dispatch(removeUser(id));
      toaster.push(
        <Message type="success" duration={5000} showIcon>
          {t('Deleted successfully')}
        </Message>,
        { placement: 'topCenter' }
      );
      // Re-fetch all users for total count
    } catch (error) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(error.message)}
        </Message>,
        { placement: 'topCenter' }
      );
    }
    setConfirmModal({ open: false });
  };

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <TagPicker
            data={usersList?.map(item => ({
              ...item,
              label: item.firstname + ' ' + item.lastname,
              value: item._id
            }))}
            placeholder={t('Firstname Lastname')}
            value={searchUsers}
            onChange={setSearchUsers}
            style={{ width: 223 }}
          />
          <InputGroup inside>
            <Input
              placeholder={t('Registration number or car number')}
              value={searchKeyword}
              onChange={setSearchKeyword}
            />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
          <SelectPicker
            label="Grade"
            data={grades}
            searchable={false}
            value={grade}
            onChange={setGrade}
          />
          <SelectPicker
            label={t('Team')}
            data={servicelines}
            searchable={false}
            value={sl}
            onChange={setSl}
          />
          <SelectPicker
            label={t('Status')}
            data={[
              { label: t('Active'), value: false, icon: <RiUserFollowFill /> },
              { label: t('Blocked'), value: true, icon: <RiUserForbidFill /> }
            ]}
            searchable={false}
            value={status}
            onChange={setStatus}
            renderMenuItem={renderMenuItem}
          />
        </Stack>
        <Stack spacing={5}>
          {limit == 'all' && (
            <Whisper
              placement="top"
              controlId="control-id-revert"
              trigger="hover"
              speaker={<Tooltip>{t('Revert to paginated view')}</Tooltip>}
            >
              <IconButton
                appearance="primary"
                icon={<Icon as={GrRevert} />}
                onClick={handleRevertToPaginated}
              >
                {t('Back to Paginated View')}
              </IconButton>
            </Whisper>
          )}
          {checkedKeys?.length > 0 && (
            <Whisper
              placement="top"
              controlId="control-id-hover"
              trigger="hover"
              speaker={<Tooltip>{t('Update selected users')}</Tooltip>}
            >
              <IconButton
                icon={<Icon as={FaUserEdit} />}
                onClick={() => setOpenUpdate(true)}
              ></IconButton>
            </Whisper>
          )}
          <Whisper
            placement="top"
            controlId="control-id-hover"
            trigger="hover"
            speaker={<Tooltip>{t('Export')}</Tooltip>}
          >
            <IconButton
              icon={<Icon as={CiExport} />}
              onClick={handleExport}
              loading={loadingExport}
              disabled={loadingExport}
            ></IconButton>
          </Whisper>
          <Whisper
            placement="top"
            controlId="control-id-hover"
            trigger="hover"
            speaker={<Tooltip>{t('Import Excel file')}</Tooltip>}
          >
            <IconButton icon={<Icon as={CiImport} />} onClick={() => setOpen(true)}></IconButton>
          </Whisper>
          <IconButton
            appearance="primary"
            icon={<Icon as={MdPersonAdd} />}
            onClick={() => setShowDrawer(true)}
          >
            {t('Add Member')}
          </IconButton>
          <Button appearance="primary" onClick={handleNotificationSettings}>
            <Icon as={Gear} style={{ marginRight: 8 }} /> {t('Settings')}
          </Button>
        </Stack>
      </Stack>

      <Panel bodyFill>
        <Table
          height={Math.max(getHeight(window) - 250, 400)}
          data={data}
          sortColumn={sortColumn}
          sortType={sortType}
          onSortColumn={handleSortColumn}
          loading={loading}
        >
          <Column width={50} align="center">
            <HeaderCell style={{ padding: 0 }}>
              <div style={{ lineHeight: '40px' }}>
                <Checkbox
                  inline
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={handleCheckAll}
                />
              </div>
            </HeaderCell>
            <CheckCell dataKey="_id" checkedKeys={checkedKeys} onChange={handleCheck} />
          </Column>
          <Column width={80} align="center">
            <HeaderCell>{t('Avatar')}</HeaderCell>
            <ImageCell dataKey="photo" />
          </Column>
          <Column minWidth={160} flexGrow={1} sortable resizable>
            <HeaderCell>{t('Name')}</HeaderCell>
            <NameCell dataKey="name" />
          </Column>
          <Column width={230} sortable resizable>
            <HeaderCell>{t('Registration Number')}</HeaderCell>
            <Cell dataKey="registrationNumber" />
          </Column>
          <Column width={200} sortable resizable>
            <HeaderCell>{t('Grade')}</HeaderCell>
            <Cell>{rowData => <p>{rowData.grade?.grade_name || ''}</p>}</Cell>
          </Column>
          <Column width={240} sortable resizable>
            <HeaderCell>{t('Team')}</HeaderCell>
            <Cell>{rowData => <p>{rowData.serviceLine?.serviceLine || ''}</p>}</Cell>
          </Column>
          <Column width={50} flexGrow={0.5} fixed="right">
            <HeaderCell>
              <MoreIcon />
            </HeaderCell>
            <Cell style={{ padding: '6px' }}>
              {rowData => (
                <Stack>
                  <IconButton
                    onClick={() => navigate(`/member/${rowData._id}`)}
                    size="xs"
                    icon={<Edit color="blue" />}
                    id={`edit-${rowData._id}`}
                  />
                  {!rowData.forgotten && (
                    <IconButton
                      size="xs"
                      icon={<MdOutlineDeleteOutline color="red" />}
                      onClick={() => {
                        setConfirmModal({
                          open: true,
                          title: t('Are you sure you want to remove this user’s personal data?'),
                          subtitle: (
                            <span style={{ color: 'red', fontWeight: 'bold' }}>
                              {t('This action is permanent and cannot be undone!')}
                            </span>
                          ),
                          onConfirm: () => handleDelete(rowData._id)
                        });
                      }}
                      id={`delete-${rowData._id}`}
                    />
                  )}
                  {!rowData.forgotten && (
                    <Whisper
                      placement="top"
                      controlId={`control-id-block-${rowData._id}`}
                      trigger="hover"
                      speaker={
                        <Tooltip>
                          {t(
                            rowData.isBlocked
                              ? 'Unblock user'
                              : 'Set resignation date and block user'
                          )}
                        </Tooltip>
                      }
                    >
                      <IconButton
                        size="xs"
                        icon={
                          rowData.isBlocked ? (
                            <TbLockCheck color="green" />
                          ) : (
                            <TbLockCancel color="red" />
                          )
                        }
                        onClick={() => {
                          if (rowData.isBlocked) {
                            setConfirmModal({
                              open: true,
                              title: t('Are you sure you want to unblock this user?'),
                              subtitle: (
                                <div style={{ lineHeight: '1.5', marginBottom: 10 }}>
                                  {t('This will restore the user’s access.')}
                                  <div style={{ marginTop: 5 }}>
                                    {rowData.resignation_date
                                      ? t('Resignation date: {{date}}', {
                                          date: new Date(rowData.resignation_date)
                                            .toLocaleDateString('en-GB', {
                                              day: '2-digit',
                                              month: '2-digit',
                                              year: 'numeric'
                                            })
                                            .replace(/\//g, '-')
                                        })
                                      : t('Resignation date not set yet')}
                                  </div>
                                </div>
                              ),
                              onConfirm: () => handleToggleBlock(rowData._id, rowData.isBlocked),
                              id: rowData._id
                            });
                          } else {
                            setConfirmModal({
                              open: true,
                              title: t('Block User'),
                              subtitle: true,
                              onConfirm: resignationDate => {
                                handleSetResignationAndBlock(rowData._id, resignationDate);
                              },
                              id: rowData._id
                            });
                          }
                        }}
                        id={`block-${rowData._id}`}
                      />
                    </Whisper>
                  )}
                </Stack>
              )}
            </Cell>
          </Column>
        </Table>
        <div style={{ padding: 20 }}>
          {limit == 'all' ? (
            <Button appearance="primary" onClick={handleRevertToPaginated}>
              {t('Back to Paginated View')}
            </Button>
          ) : (
            <Pagination
              prev
              next
              first
              last
              ellipsis
              boundaryLinks
              maxButtons={5}
              size="xs"
              layout={['total', '-', 'limit', '|', 'pager', 'skip']}
              total={allUsers}
              limitOptions={[10, 20, 50, 'all']}
              limit={limit}
              activePage={page}
              onChangePage={setPage}
              onChangeLimit={handleChangeLimit}
            />
          )}
        </div>
      </Panel>

      <Modal open={showSelectAllModal} onClose={() => setShowSelectAllModal(false)} size="xs">
        <Modal.Header>
          <Modal.Title>{t('Select Members')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {t('You’ve selected {{count}} members on this page!', {
              count: data.length
            })}
          </p>
          <p>
            {t('Want to select all {{total}} members across every page?', {
              total: allUsers
            })}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleSelectCurrentPage} appearance="subtle">
            {t('Select Current Page')}
          </Button>
          <Button onClick={handleSelectAllData} appearance="primary">
            {t('Select All')}
          </Button>
        </Modal.Footer>
      </Modal>

      {showDrawer && <DrawerView open={showDrawer} onClose={() => setShowDrawer(false)} />}
      {openUpdate && (
        <DrawerUpdate
          open={openUpdate}
          onClose={() => setOpenUpdate(false)}
          checkedKeys={checkedKeys}
          setCheckedKeys={setCheckedKeys}
        />
      )}
      <NotificationSettingsDrawer
        open={showNotificationDrawer}
        onClose={() => setShowNotificationDrawer(false)}
        type="block_users"
      />
      {confirmModal.open && (
        <ConfirmModal confirmDialog={confirmModal} setConfirmDialog={setConfirmModal} />
      )}
      {open && <ModalUsersMass open={open} setOpen={setOpen} />}
    </>
  );
};

export default DataTable;
